import { defineStore } from 'pinia';
import routesApi from '@/api/routes';

const ROUTES_CACHE_PREFIX = 'mowapp.routes.cache.v1';
const ROUTE_MUTATION_QUEUE_KEY = 'mowapp.routes.mutationQueue.v1';

function routesListCacheKey(params = {}) {
  return `${ROUTES_CACHE_PREFIX}.list.${JSON.stringify(params || {})}`;
}

function routeDetailCacheKey(id) {
  return `${ROUTES_CACHE_PREFIX}.detail.${id}`;
}

function saveCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
  } catch {
    // Ignore quota and storage access failures.
  }
}

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.value ?? null;
  } catch {
    return null;
  }
}

function isNetworkError(err) {
  return !err?.response;
}

function nowTs() {
  return Date.now();
}

function makeMutationId() {
  return `${nowTs()}-${Math.random().toString(36).slice(2, 10)}`;
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

export const useRoutesStore = defineStore('routes', {
  state: () => ({
    routes: [],
    current: null,
    stops: [],
    loading: false,
    usingOfflineData: false,
    lastSyncedAt: null,
    mutationQueue: [],
    syncingQueue: false,
    offlineSyncReady: false,
    isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
    onlineListener: null,
    offlineListener: null,
  }),

  getters: {
    pendingMutations(state) {
      return state.mutationQueue.filter((m) => ['pending', 'syncing', 'retrying'].includes(m.status));
    },
    conflictingMutations(state) {
      return state.mutationQueue.filter((m) => m.status === 'conflict');
    },
    failedMutations(state) {
      return state.mutationQueue.filter((m) => m.status === 'failed');
    },
  },

  actions: {
    initOfflineSync() {
      if (this.offlineSyncReady) return;
      this.offlineSyncReady = true;
      this.hydrateMutationQueue();

      if (typeof window !== 'undefined') {
        if (!this.onlineListener) this.onlineListener = () => this.handleOnline();
        if (!this.offlineListener) this.offlineListener = () => this.handleOffline();
        window.addEventListener('online', this.onlineListener);
        window.addEventListener('offline', this.offlineListener);
      }

      if (!this.isOffline) {
        this.processQueue();
      }
    },

    handleOnline() {
      this.isOffline = false;
      this.processQueue();
    },

    handleOffline() {
      this.isOffline = true;
    },

    hydrateMutationQueue() {
      try {
        const raw = localStorage.getItem(ROUTE_MUTATION_QUEUE_KEY);
        if (!raw) {
          this.mutationQueue = [];
          return;
        }
        const parsed = JSON.parse(raw);
        this.mutationQueue = Array.isArray(parsed) ? parsed : [];
      } catch {
        this.mutationQueue = [];
      }
    },

    persistMutationQueue() {
      try {
        localStorage.setItem(ROUTE_MUTATION_QUEUE_KEY, JSON.stringify(this.mutationQueue));
      } catch {
        // Ignore localStorage persistence errors.
      }
    },

    trimMutationQueue() {
      const sorted = [...this.mutationQueue].sort((a, b) => b.createdAt - a.createdAt);
      this.mutationQueue = sorted.slice(0, 100);
    },

    describeMutation(mutation) {
      const map = {
        createRoute: 'Create route',
        updateRoute: 'Update route details',
        updateStop: 'Update stop status',
        reorderStops: 'Reorder route stops',
        addStop: 'Add route stop',
        removeStop: 'Remove route stop',
        optimizeRoute: 'Optimize route',
      };
      return map[mutation.type] || mutation.type;
    },

    enqueueMutation(type, payload, options = {}) {
      const entry = {
        id: makeMutationId(),
        type,
        payload: clone(payload),
        status: 'pending',
        createdAt: nowTs(),
        updatedAt: nowTs(),
        error: null,
        meta: options.meta || {},
      };

      this.mutationQueue = [entry, ...this.mutationQueue];
      this.trimMutationQueue();
      this.persistMutationQueue();
      return entry;
    },

    setMutationState(id, patch) {
      const idx = this.mutationQueue.findIndex((m) => m.id === id);
      if (idx === -1) return;
      this.mutationQueue[idx] = {
        ...this.mutationQueue[idx],
        ...patch,
        updatedAt: nowTs(),
      };
      this.persistMutationQueue();
    },

    removeMutation(id) {
      this.mutationQueue = this.mutationQueue.filter((m) => m.id !== id);
      this.persistMutationQueue();
    },

    retryMutation(id) {
      this.setMutationState(id, {
        status: 'pending',
        error: null,
      });
      this.processQueue();
    },

    clearSyncedMutations() {
      this.mutationQueue = this.mutationQueue.filter((m) => m.status !== 'synced');
      this.persistMutationQueue();
    },

    updateCachedCurrentDetail() {
      if (!this.current?.id) return;
      saveCache(routeDetailCacheKey(this.current.id), {
        route: this.current,
        stops: this.stops,
      });
    },

    applyOptimisticMutation(type, payload) {
      if (type === 'updateStop') {
        const idx = this.stops.findIndex((s) => s.id === payload.stopId);
        if (idx !== -1) {
          this.stops[idx] = {
            ...this.stops[idx],
            status: payload.stopData.status,
            skip_reason: payload.stopData.skipReason || null,
          };
          this.updateCachedCurrentDetail();
        }
        return;
      }

      if (type === 'reorderStops') {
        const orderMap = new Map(payload.stopIds.map((id, i) => [id, i + 1]));
        this.stops = [...this.stops]
          .sort((a, b) => (orderMap.get(a.id) || 99999) - (orderMap.get(b.id) || 99999))
          .map((s) => ({ ...s, stop_order: orderMap.get(s.id) || s.stop_order }));
        this.updateCachedCurrentDetail();
        return;
      }

      if (type === 'removeStop') {
        this.stops = this.stops
          .filter((s) => s.id !== payload.stopId)
          .map((s, i) => ({ ...s, stop_order: i + 1 }));
        this.updateCachedCurrentDetail();
        return;
      }

      if (type === 'updateRoute') {
        const updates = {
          ...(payload.routeData.name !== undefined ? { name: payload.routeData.name } : {}),
          ...(payload.routeData.scheduledDate !== undefined ? { scheduled_date: payload.routeData.scheduledDate } : {}),
          ...(payload.routeData.status !== undefined ? { status: payload.routeData.status } : {}),
          ...(payload.routeData.repeatType !== undefined ? { repeat_type: payload.routeData.repeatType } : {}),
          ...(payload.routeData.repeatUntil !== undefined ? { repeat_until: payload.routeData.repeatUntil } : {}),
        };

        if (this.current?.id === payload.routeId) {
          this.current = { ...this.current, ...updates };
          this.updateCachedCurrentDetail();
        }

        const routeIdx = this.routes.findIndex((r) => r.id === payload.routeId);
        if (routeIdx !== -1) {
          this.routes[routeIdx] = { ...this.routes[routeIdx], ...updates };
        }
      }
    },

    async runMutation(type, payload) {
      if (type === 'createRoute') {
        return routesApi.create(payload.routeData);
      }
      if (type === 'updateRoute') {
        return routesApi.update(payload.routeId, payload.routeData);
      }
      if (type === 'updateStop') {
        return routesApi.updateStop(payload.routeId, payload.stopId, payload.stopData);
      }
      if (type === 'reorderStops') {
        return routesApi.reorderStops(payload.routeId, payload.stopIds);
      }
      if (type === 'addStop') {
        return routesApi.addStop(payload.routeId, payload.stopData);
      }
      if (type === 'removeStop') {
        return routesApi.removeStop(payload.routeId, payload.stopId);
      }
      if (type === 'optimizeRoute') {
        return routesApi.optimize(payload.routeId);
      }
      throw new Error('Unknown mutation type');
    },

    async applyServerResult(mutation, response) {
      const data = response?.data;
      const routeId = mutation?.payload?.routeId;

      if (mutation.type === 'updateRoute' && data?.route) {
        if (this.current?.id === routeId) {
          this.current = data.route;
          this.updateCachedCurrentDetail();
        }
        const routeIdx = this.routes.findIndex((r) => r.id === routeId);
        if (routeIdx !== -1) this.routes[routeIdx] = { ...this.routes[routeIdx], ...data.route };
      }

      if (mutation.type === 'updateStop' && data?.stop) {
        const stopIdx = this.stops.findIndex((s) => s.id === mutation.payload.stopId);
        if (stopIdx !== -1) {
          this.stops[stopIdx] = { ...this.stops[stopIdx], ...data.stop };
          this.updateCachedCurrentDetail();
        }
      }

      if (mutation.type === 'reorderStops' && Array.isArray(data?.stops) && this.current?.id === routeId) {
        this.stops = data.stops;
        this.updateCachedCurrentDetail();
      }

      if (mutation.type === 'addStop' && Array.isArray(data?.stops) && this.current?.id === routeId) {
        this.stops = data.stops;
        this.updateCachedCurrentDetail();
      }

      if (mutation.type === 'removeStop' && this.current?.id === routeId) {
        if (Array.isArray(data?.stops)) {
          this.stops = data.stops;
        } else {
          this.stops = this.stops.filter((s) => s.id !== mutation.payload.stopId);
        }
        this.updateCachedCurrentDetail();
      }

      if (mutation.type === 'optimizeRoute' && data?.route && Array.isArray(data?.stops) && this.current?.id === routeId) {
        this.current = data.route;
        this.stops = data.stops;
        this.updateCachedCurrentDetail();
      }

      if (mutation.type === 'createRoute') {
        this.lastSyncedAt = nowTs();
      }
    },

    async processQueue() {
      if (this.syncingQueue || this.isOffline) return;
      this.syncingQueue = true;

      try {
        let hasNext = true;
        while (hasNext) {
          const next = this.mutationQueue.find((m) => ['pending', 'retrying'].includes(m.status));
          if (!next) {
            hasNext = false;
            continue;
          }

          this.setMutationState(next.id, { status: 'syncing', error: null });

          try {
            const response = await this.runMutation(next.type, next.payload);
            await this.applyServerResult(next, response);
            this.setMutationState(next.id, { status: 'synced', syncedAt: nowTs(), error: null });
            this.lastSyncedAt = nowTs();
          } catch (err) {
            if (isNetworkError(err)) {
              this.setMutationState(next.id, { status: 'pending', error: 'Waiting for connection' });
              this.isOffline = true;
              break;
            }

            if (err?.response?.status === 409) {
              this.setMutationState(next.id, {
                status: 'conflict',
                error: err.response?.data?.error || 'Conflict detected while syncing',
              });
            } else {
              this.setMutationState(next.id, {
                status: 'failed',
                error: err.response?.data?.error || 'Failed to sync mutation',
              });
            }
          }
        }
      } finally {
        this.syncingQueue = false;
      }
    },

    async queueOrRunMutation(type, payload, options = {}) {
      const shouldQueueImmediately = this.isOffline || (typeof navigator !== 'undefined' && !navigator.onLine);

      if (shouldQueueImmediately) {
        this.applyOptimisticMutation(type, payload);
        const queued = this.enqueueMutation(type, payload, options);
        return { queued: true, mutationId: queued.id };
      }

      try {
        const response = await this.runMutation(type, payload);
        await this.applyServerResult({ type, payload }, response);
        return { queued: false, response };
      } catch (err) {
        if (!isNetworkError(err)) throw err;
        this.isOffline = true;
        this.applyOptimisticMutation(type, payload);
        const queued = this.enqueueMutation(type, payload, options);
        return { queued: true, mutationId: queued.id };
      }
    },

    async fetchAll(params = {}) {
      this.loading = true;
      try {
        const { data } = await routesApi.getAll(params);
        this.routes = Array.isArray(data?.routes) ? data.routes : [];
        this.usingOfflineData = false;
        this.lastSyncedAt = Date.now();
        saveCache(routesListCacheKey(params), this.routes);
      } catch (err) {
        if (!isNetworkError(err)) throw err;

        const cachedRoutes = readCache(routesListCacheKey(params));
        if (Array.isArray(cachedRoutes)) {
          this.routes = cachedRoutes;
          this.usingOfflineData = true;
          return;
        }
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchOne(id) {
      this.loading = true;
      try {
        const { data } = await routesApi.getOne(id);
        this.current = data.route;
        this.stops = Array.isArray(data?.stops) ? data.stops : [];
        this.usingOfflineData = false;
        this.lastSyncedAt = Date.now();
        saveCache(routeDetailCacheKey(id), {
          route: this.current,
          stops: this.stops,
        });
        return data;
      } catch (err) {
        if (!isNetworkError(err)) throw err;

        const cached = readCache(routeDetailCacheKey(id));
        if (cached?.route) {
          this.current = cached.route;
          this.stops = Array.isArray(cached?.stops) ? cached.stops : [];
          this.usingOfflineData = true;
          return cached;
        }
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async create(routeData) {
      const result = await this.queueOrRunMutation('createRoute', { routeData });
      if (result.queued) return { queued: true };
      return result.response.data;
    },

    async updateRoute(routeId, routeData) {
      const result = await this.queueOrRunMutation('updateRoute', { routeId, routeData });
      if (result.queued) return { queued: true };
      return result.response.data;
    },

    async optimize(id) {
      this.loading = true;
      try {
        const result = await this.queueOrRunMutation('optimizeRoute', { routeId: id });
        if (result.queued) {
          return { queued: true, route: this.current, stops: this.stops };
        }
        const { data } = result.response;
        this.current = data.route || this.current;
        this.stops = Array.isArray(data?.stops) ? data.stops : this.stops;
        this.updateCachedCurrentDetail();
        return data;
      } finally {
        this.loading = false;
      }
    },

    async addStop(routeId, stopData) {
      const result = await this.queueOrRunMutation('addStop', { routeId, stopData });
      if (result.queued) return { queued: true, stops: this.stops };
      return result.response.data;
    },

    async removeStop(routeId, stopId) {
      const result = await this.queueOrRunMutation('removeStop', { routeId, stopId });
      if (result.queued) return { queued: true, stops: this.stops };
      return result.response.data;
    },

    async reorderStops(routeId, stopIds) {
      const result = await this.queueOrRunMutation('reorderStops', { routeId, stopIds });
      if (result.queued) return { queued: true, stops: this.stops };
      return result.response.data;
    },

    async updateStop(routeId, stopId, stopData) {
      const result = await this.queueOrRunMutation('updateStop', { routeId, stopId, stopData });
      if (result.queued) {
        const idx = this.stops.findIndex((s) => s.id === stopId);
        if (idx !== -1) {
          this.stops[idx] = {
            ...this.stops[idx],
            status: stopData.status,
            skip_reason: stopData.skipReason || null,
          };
          this.updateCachedCurrentDetail();
        }
        return { queued: true, stop: this.stops[idx] || null };
      }

      const { data } = result.response;
      const idx = this.stops.findIndex((s) => s.id === stopId);
      if (idx !== -1) {
        this.stops[idx] = { ...this.stops[idx], ...data.stop };
        this.updateCachedCurrentDetail();
      }
      return data.stop;
    },
  },
});
